import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { invokeApi } from '../lib/invokeApi';
import { useAuth } from './AuthContext';
import { useUser } from './UserContext';

export interface BatchCartItem {
  localId: string;
  issuerId: string;
  issuerName: string;
  reviewType: string;
  reviewTypeLabel: string;
  addedAt: string;
}

interface BatchCart {
  items: BatchCartItem[];
  aiProvider: string | null;
}

interface SubmitResult {
  success: boolean;
  dryRun: boolean;
  jobsCreated: number;
  batchId?: string;
  itemsByReviewType: Record<string, number>;
  skipped: Array<{ issuerId: string; reviewType: string; reason: string }>;
  errors: string[];
}

interface BatchCartContextValue {
  cart: BatchCart;
  addItem: (item: Omit<BatchCartItem, 'localId' | 'addedAt'>) => void;
  removeItem: (localId: string) => void;
  clearCart: () => void;
  setProvider: (provider: string) => void;
  itemCount: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  submitBatch: (dryRun?: boolean) => Promise<SubmitResult>;
  submitting: boolean;
  submitResult: SubmitResult | null;
  clearResult: () => void;
}

const STORAGE_KEY = 'batch_cart';

function generateId(): string {
  return crypto.randomUUID();
}

function loadCart(): BatchCart {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore parse errors
  }
  return { items: [], aiProvider: null };
}

function saveCart(cart: BatchCart): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  } catch {
    // ignore storage errors
  }
}

const BatchCartContext = createContext<BatchCartContextValue | null>(null);

export function BatchCartProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { activeTeam } = useUser();
  const [cart, setCart] = useState<BatchCart>(loadCart);
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);

  // Persist cart to sessionStorage on changes
  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const addItem = useCallback((item: Omit<BatchCartItem, 'localId' | 'addedAt'>) => {
    setCart(prev => {
      // Check for duplicate (same issuer + review type)
      const exists = prev.items.some(
        i => i.issuerId === item.issuerId && i.reviewType === item.reviewType
      );
      if (exists) return prev;

      const newItem: BatchCartItem = {
        ...item,
        localId: generateId(),
        addedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        items: [...prev.items, newItem],
        // Lock provider on first item if not already set
        aiProvider: prev.aiProvider || 'grok',
      };
    });
  }, []);

  const removeItem = useCallback((localId: string) => {
    setCart(prev => {
      const newItems = prev.items.filter(i => i.localId !== localId);
      return {
        ...prev,
        items: newItems,
        // Clear provider if cart is empty
        aiProvider: newItems.length === 0 ? null : prev.aiProvider,
      };
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({ items: [], aiProvider: null });
    setSubmitResult(null);
  }, []);

  const setProvider = useCallback((provider: string) => {
    setCart(prev => ({ ...prev, aiProvider: provider }));
  }, []);

  const clearResult = useCallback(() => {
    setSubmitResult(null);
  }, []);

  const submitBatch = useCallback(async (dryRun = false): Promise<SubmitResult> => {
    if (cart.items.length === 0) {
      const result: SubmitResult = {
        success: false,
        dryRun,
        jobsCreated: 0,
        itemsByReviewType: {},
        skipped: [],
        errors: ['Cart is empty'],
      };
      setSubmitResult(result);
      return result;
    }

    setSubmitting(true);
    try {
      const { data: responseData, error: invokeError } = await invokeApi('submit-batch-review', {
        items: cart.items.map(i => ({
          issuerId: i.issuerId,
          reviewType: i.reviewType,
        })),
        aiProvider: cart.aiProvider || 'grok',
        teamId: activeTeam?.team_id || undefined,
        dryRun,
        chunkSize: 50,
      });

      if (invokeError) {
        const result: SubmitResult = {
          success: false,
          dryRun,
          jobsCreated: 0,
          itemsByReviewType: {},
          skipped: [],
          errors: [invokeError.message || 'Unknown error'],
        };
        setSubmitResult(result);
        return result;
      }

      const result = responseData as SubmitResult;
      setSubmitResult(result);

      // Clear cart on successful non-dry-run submission
      if (result.success && !dryRun) {
        setCart({ items: [], aiProvider: null });
      }

      return result;
    } catch (error) {
      const result: SubmitResult = {
        success: false,
        dryRun,
        jobsCreated: 0,
        itemsByReviewType: {},
        skipped: [],
        errors: [(error as Error).message],
      };
      setSubmitResult(result);
      return result;
    } finally {
      setSubmitting(false);
    }
  }, [cart, activeTeam]);

  return (
    <BatchCartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        clearCart,
        setProvider,
        itemCount: cart.items.length,
        isOpen,
        setIsOpen,
        submitBatch,
        submitting,
        submitResult,
        clearResult,
      }}
    >
      {children}
    </BatchCartContext.Provider>
  );
}

// Co-located with BatchCartProvider by convention; splitting into a separate
// file would require updating import sites outside src/hooks and src/contexts,
// which is out of scope here.
// eslint-disable-next-line react-refresh/only-export-components
export function useBatchCart() {
  const context = useContext(BatchCartContext);
  if (!context) {
    throw new Error('useBatchCart must be used within a BatchCartProvider');
  }
  return context;
}
